import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { InvoiceTemplateQueryMetadata } from '~/api/invoiceTemplates';
import type { InvoiceTemplate } from '~/api/invoiceTemplates/types';
import type { Brand } from '~/types/user';

import { useClientsWithTrashed } from '~/api/clients';
import { invoiceTemplatesQueryOptions, useInvoiceTemplateFilters } from '~/api/invoiceTemplates';
import { useUser } from '~/api/user';
import { createAppColumnHelper } from '~/components';
import { ColumnIds } from '~/constants/table';
import { useIntl } from '~/hooks/useIntl';

import { InvoiceTemplateClientCell } from './InvoiceTemplatesTableCells/InvoiceTemplateClientCell';
import { InvoiceTemplateMobileCell } from './InvoiceTemplatesTableCells/InvoiceTemplateMobileCell';
import { InvoiceTemplateNextInvoiceCell } from './InvoiceTemplatesTableCells/InvoiceTemplateNextInvoiceCell';
import { InvoiceTemplateOptionsCell } from './InvoiceTemplatesTableCells/InvoiceTemplateOptionsCell';
import { InvoiceTemplateTitleCell } from './InvoiceTemplatesTableCells/InvoiceTemplateTitleCell';
import { InvoiceTemplateTotalCell } from './InvoiceTemplatesTableCells/InvoiceTemplateTotalCell';
import { InvoiceTemplateTypeCell } from './InvoiceTemplatesTableCells/InvoiceTemplateTypeCell';

const columnHelper = createAppColumnHelper<InvoiceTemplate>();

export const useInvoiceTemplatesTableColumns = () => {
  const { data: clients } = useClientsWithTrashed();
  const filters = useInvoiceTemplatesTableFilters();

  const { t } = useTranslation(['invoiceTemplates', 'filters']);

  return useMemo(
    () =>
      columnHelper.columns([
        // Title
        columnHelper.accessor('title', {
          id: ColumnIds.invoiceTemplatesTitle,
          header: t('invoiceTemplates:overview.columns.title'),
          cell: ({ row: { original: invoiceTemplate } }) => <InvoiceTemplateTitleCell invoiceTemplate={invoiceTemplate} />,
          meta: {
            styles: { size: '3fr', minSize: 150 },
            isResizable: true,
          },
        }),

        // Brand (hidden, column filter only)
        columnHelper.accessor(({ brandId }) => brandId, {
          id: ColumnIds.invoiceTemplatesBrand,
          meta: {
            filterOptions: filters.brands,
          },
        }),

        // Client
        columnHelper.accessor(({ clientId }) => clients.find((client) => client.id === clientId)?.name ?? '', {
          id: ColumnIds.invoiceTemplatesClient,
          header: t('invoiceTemplates:overview.columns.client'),
          cell: ({ row: { original: invoiceTemplate } }) => <InvoiceTemplateClientCell invoiceTemplate={invoiceTemplate} />,
          meta: {
            styles: { size: '3fr', minSize: 125 },
            isResizable: true,
            filterOptions: filters.clients,
          },
        }),

        // Type
        columnHelper.accessor('type', {
          id: ColumnIds.invoiceTemplatesType,
          header: t('invoiceTemplates:overview.columns.type'),
          cell: ({ row: { original: invoiceTemplate } }) => <InvoiceTemplateTypeCell invoiceTemplate={invoiceTemplate} />,
          meta: {
            styles: { size: '2fr', minSize: 180 },
            isResizable: true,
            filterOptions: filters.types,
          },
        }),

        // Next invoice at
        columnHelper.accessor(({ nextInvoiceAt }) => nextInvoiceAt ?? '', {
          id: ColumnIds.invoiceTemplatesNextInvoice,
          header: t('invoiceTemplates:overview.columns.nextInvoiceAt'),
          cell: ({ row: { original: invoiceTemplate } }) => <InvoiceTemplateNextInvoiceCell invoiceTemplate={invoiceTemplate} />,
          meta: {
            styles: { size: '2fr', minSize: 150 },
            isResizable: true,
          },
        }),

        // Total CO
        columnHelper.accessor('calculationData.coExclVat', {
          id: ColumnIds.invoiceTemplatesTotal,
          header: t('invoiceTemplates:overview.columns.total'),
          cell: ({ row: { original: invoiceTemplate } }) => <InvoiceTemplateTotalCell invoiceTemplate={invoiceTemplate} />,
          meta: {
            styles: { size: 'max-content', minSize: 120, justify: 'end' },
            isResizable: true,
          },
        }),

        // Options
        columnHelper.display({
          id: ColumnIds.options,
          cell: ({ row: { original: invoiceTemplate } }) => <InvoiceTemplateOptionsCell invoiceTemplate={invoiceTemplate} />,
          meta: {
            styles: { size: 'max-content', minSize: 40 },
          },
        }),

        // Mobile
        columnHelper.display({
          id: ColumnIds.invoiceTemplatesMobile,
          cell: ({ row: { original: invoiceTemplate } }) => <InvoiceTemplateMobileCell invoiceTemplate={invoiceTemplate} />,
          meta: { mobile: true },
        }),
      ]),
    [clients, filters, t],
  );
};

export const usePrefetchNextPage = (nextPage: number | null, queryMetadata: InvoiceTemplateQueryMetadata) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (nextPage === null) return;

    queryClient.prefetchQuery(
      invoiceTemplatesQueryOptions({
        ...queryMetadata,
        pagination: { ...queryMetadata.pagination, pageIndex: queryMetadata.pagination.pageIndex + 1 },
      }),
    );
  }, [nextPage, queryClient, queryMetadata]);
};

const useInvoiceTemplatesTableFilters = () => {
  const { data: user } = useUser();
  const { data: clients } = useClientsWithTrashed();
  const { data: invoiceTemplateFilters } = useInvoiceTemplateFilters();

  const { compareForSort } = useIntl();
  const { t } = useTranslation(['filters', 'invoiceTemplates']);

  const clientFilterOptions = useMemo(
    () =>
      invoiceTemplateFilters.clientIds
        .map((clientId) => ({
          value: clientId,
          label: clients.find((client) => client.id === clientId)?.name ?? '',
        }))
        .sort((a, b) => compareForSort(a.label, b.label)),
    [clients, compareForSort, invoiceTemplateFilters.clientIds],
  );

  const brandFilterOptions = useMemo(
    () =>
      invoiceTemplateFilters.brandIds
        .map((brandId) => user.brands.find((brand) => brand.id === brandId) ?? null)
        .filter((brand): brand is Brand => brand !== null)
        .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary) || compareForSort(a.companyName, b.companyName))
        .map((brand) => ({ value: brand.id, label: brand.companyName })),
    [compareForSort, invoiceTemplateFilters.brandIds, user.brands],
  );

  const typeFilterOptions = useMemo(
    () =>
      invoiceTemplateFilters.types.map((type) => ({
        value: type,
        label: t(`invoiceTemplates:terminology.templateType.cases.${type}`),
      })),
    [invoiceTemplateFilters.types, t],
  );

  return useMemo(
    () => ({
      clients: clientFilterOptions,
      brands: brandFilterOptions,
      types: typeFilterOptions,
    }),
    [brandFilterOptions, clientFilterOptions, typeFilterOptions],
  );
};
